/**
 * Sticky add-to-cart bar — HopHopShop.
 *
 * Renders two bars (desktop top, mobile bottom). Both reveal when the
 * inline <product-form> ATC scrolls out of viewport. The bar's CTA reads
 * the current variant id from the inline form and adds via the shared
 * addToCart() helper.
 *
 * Listens for `product:variant-changed` to keep the price label in sync,
 * and `product:size-required` to pulse a pink "SELECT A SIZE" message for
 * 2 seconds in place of the price (matches PDP behavior).
 */
import { Component } from "@theme/component";
import { addToCart, announceCartStatus } from "@theme/cart-add";

class StickyAtc extends Component {
  static ERROR_RESET_MS = 2000;
  static PROMPT_MS = 2000;

  setup() {
    this.bars = this.$$("[data-bar]");
    this.priceEls = this.$$("[data-price]");
    this.comparePriceEls = this.$$("[data-compare-price]");
    this.variantEls = this.$$("[data-variant-label]");
    this.variantEl = this.variantEls[0] || null;
    this.ctaButtons = this.$$("[data-add-to-cart]");
    this.ctaLabels = this.$$("[data-cta-label]");
    this.inlineForm = document.querySelector("product-form form");
    if (!this.bars.length || !this.inlineForm) return;

    this.t = {
      addToBag: this.dataset.i18nAddToBag || "ADD TO BAG",
      outOfStock: this.dataset.i18nOutOfStock || "OUT OF STOCK",
      notAvailable: this.dataset.i18nNotAvailable || "NOT AVAILABLE",
      selectSize: this.dataset.i18nSelectSize || "SELECT A SIZE",
    };

    const triggerEl = this.inlineForm.querySelector('[type="submit"]');
    if (!triggerEl) return;

    this.inlineOut = false;
    this.footerIn = false;
    this.atcHasBeenVisible = false;

    new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) this.atcHasBeenVisible = true;
        this.inlineOut = !entry.isIntersecting;
        this.#applyVisibility();
      },
      { rootMargin: "-100px 0px 0px 0px" },
    ).observe(triggerEl);

    const footerEl = document.querySelector("footer");
    if (footerEl) {
      new IntersectionObserver(
        ([entry]) => {
          this.footerIn = entry.isIntersecting;
          this.#applyVisibility();
        },
        { rootMargin: "0px 0px 0px 0px", threshold: 0 },
      ).observe(footerEl);
    }

    this.ctaButtons.forEach((btn) =>
      btn.addEventListener("click", (e) => this.#handleClick(e, btn)),
    );

    this.#bindSizePills();

    document.addEventListener("product:variant-changed", (e) => {
      this.#syncVariant(e.detail);
      this.#syncSizePills();
    });
    document.addEventListener("product:size-required", () =>
      this.#flashSizePrompt(),
    );

    this.#syncVariant({ variant: this.#readSelectedVariant() });
    this.#syncSizePills();
  }

  /**
   * Picking a size in the bar (pill on desktop, dropdown on mobile) drives the
   * inline PDP size button of the same option/value, so the whole variant flow
   * stays a single source of truth. The resulting `product:variant-changed`
   * re-syncs price + the bar's size controls.
   */
  #bindSizePills() {
    this.$$("[data-satc-size]").forEach((pill) => {
      pill.addEventListener("click", () => {
        if (pill.getAttribute("aria-disabled") === "true") return;
        const inline = this.#inlineOptionButton(
          pill.dataset.optionPosition,
          pill.dataset.optionValue,
        );
        if (inline) inline.click();
      });
    });

    this.$$("[data-satc-size-select]").forEach((select) => {
      select.addEventListener("change", () => {
        const inline = this.#inlineOptionButton(
          select.dataset.optionPosition,
          select.value,
        );
        if (inline) inline.click();
      });
    });
  }

  /** Mirror the inline size selection onto the bar's pills and dropdowns. */
  #syncSizePills() {
    this.$$("[data-satc-size]").forEach((pill) => {
      const inline = this.#inlineOptionButton(
        pill.dataset.optionPosition,
        pill.dataset.optionValue,
      );
      pill.classList.toggle(
        "is-active",
        !!inline?.classList.contains("is-active"),
      );
    });

    this.$$("[data-satc-size-select]").forEach((select) => {
      const pos = select.dataset.optionPosition;
      const active = document.querySelector(
        `product-form [data-option-input][data-option-position="${pos}"].is-active`,
      );
      const val = active?.getAttribute("data-option-value");
      if (val != null) select.value = val;
    });
  }

  #inlineOptionButton(pos, val) {
    if (pos == null || val == null) return null;
    const escaped = window.CSS?.escape ? CSS.escape(val) : val;
    return document.querySelector(
      `product-form [data-option-input][data-option-position="${pos}"][data-option-value="${escaped}"]`,
    );
  }

  #setVisible(visible) {
    this.bars.forEach((bar) => {
      bar.dataset.visible = String(visible);
      bar.setAttribute("aria-hidden", String(!visible));
    });
  }

  #applyVisibility() {
    this.#setVisible(
      this.inlineOut && !this.footerIn && this.atcHasBeenVisible,
    );
  }

  #readSelectedVariant() {
    // Read the current variant id from the inline form
    const idInput = this.inlineForm.querySelector("[data-variant-id]");
    const id = idInput?.value;
    if (!id) return null;
    try {
      const data = document.querySelector("[data-variant-data]");
      const variants = JSON.parse(data?.textContent || "[]");
      return variants.find((v) => String(v.id) === String(id)) || null;
    } catch {
      return null;
    }
  }

  #syncVariant({ variant, selected } = {}) {
    if (!variant) return;
    const oos = !variant.available;

    this.dataset.oos = String(oos);

    const onSale =
      !oos &&
      variant.compare_at_price &&
      variant.compare_at_price > variant.price;
    this.priceEls.forEach((el) => {
      el.classList.remove("is-prompt");
      el.classList.toggle("is-oos", oos);
      el.textContent = oos ? this.t.outOfStock : this.#money(variant.price);
      el.style.color = onSale ? "var(--color-pink-deep)" : "";
    });

    const comparePrice = variant.compare_at_price;
    const hasCompare = !oos && comparePrice && comparePrice > variant.price;
    this.comparePriceEls.forEach((el) => {
      if (hasCompare) {
        el.textContent = this.#money(comparePrice);
        el.removeAttribute("hidden");
      } else {
        el.setAttribute("hidden", "");
      }
    });

    const variantText = selected
      ? Object.values(selected).join(" · ")
      : variant.title === "Default Title"
        ? ""
        : variant.title;
    this.variantEls.forEach((el) => {
      el.textContent = variantText;
    });

    this.ctaButtons.forEach((btn) => {
      const isMobile = btn.closest("[data-variant='mobile']");
      btn.disabled = oos;
      btn.setAttribute("aria-disabled", String(oos));
      const label = btn.querySelector("[data-cta-label]");
      if (label) {
        label.textContent = oos ? this.t.notAvailable : this.t.addToBag;
      }
    });
  }

  #flashSizePrompt() {
    this.#setVisible(true);
    this.priceEls.forEach((el) => {
      el._prev = el.textContent;
      el.textContent = this.t.selectSize;
      el.classList.add("is-prompt");
    });
    setTimeout(() => {
      this.priceEls.forEach((el) => {
        el.classList.remove("is-prompt");
        if (el._prev) el.textContent = el._prev;
      });
    }, StickyAtc.PROMPT_MS);
  }

  #money(cents) {
    const tpl = window.Shopify?.money_format || "${{amount}}";
    const amount = Number(cents) / 100;
    const locale = window.Shopify?.locale || "en";

    const fmt = (loc, decimals) =>
      new Intl.NumberFormat(loc, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });

    const grouped2 = fmt(locale, 2).format(amount);
    const grouped0 = fmt(locale, 0).format(Math.round(amount));
    // de-DE always uses "." groups + "," decimal — borrow its formatter
    // for the *_with_comma_separator placeholders.
    const grouped2EU = fmt("de-DE", 2).format(amount);
    const grouped0EU = fmt("de-DE", 0).format(Math.round(amount));

    return tpl
      .replace("{{amount}}", grouped2)
      .replace("{{amount_no_decimals}}", grouped0)
      .replace("{{amount_with_comma_separator}}", grouped2EU)
      .replace("{{amount_no_decimals_with_comma_separator}}", grouped0EU);
  }

  async #handleClick(e, button) {
    e.preventDefault();
    // Defer to the inline form's submit, so the size-required flow stays a
    // single source of truth (it also dispatches `product:size-required` on miss).
    this.inlineForm.requestSubmit
      ? this.inlineForm.requestSubmit()
      : this.inlineForm.dispatchEvent(
          new Event("submit", { cancelable: true }),
        );
  }
}

customElements.define("sticky-atc", StickyAtc);

export { StickyAtc };
